import type { PaymentIntent } from "@chargebee/chargebee-js-types";
import { LogSentryException } from "@shapr3d/frontend-lib";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";

import type { PaymentUpdateFormData } from "../components/PaymentUpdateModal/PaymentUpdateModal";
import { queryKeys } from "../config";
import { authorizePaymentIntent, type PaymentType } from "../lib/ChargebeeAdapter";
import { UMSrequestClient } from "../stores/requestClient";
import type { BillingDetails } from "./useCheckout";

export const billingQueryKey = [...queryKeys.authenticatedQueryKey, "billing"];

interface PaymentSourceBase {
  teamID: string;
  type: PaymentType;
}

interface PaymentSourceApplePay extends PaymentSourceBase {
  type: typeof PaymentType.ApplePay;
}

interface PaymentSourceCard extends PaymentSourceBase {
  type: typeof PaymentType.Card;
  cardBrand: string;
  lastFourDigits: string;
  expirationMonth: number;
  expirationYear: number;
}

export type PaymentSource = PaymentSourceApplePay | PaymentSourceCard;

export type TeamsBillingResponseData = {
  billingDetails?: BillingDetails;
  primaryPaymentSource?: PaymentSource;
};

export const useBilling = (teamID: string | undefined): UseQueryResult<TeamsBillingResponseData> =>
  useQuery({
    queryKey: [...billingQueryKey, teamID],
    queryFn: ({ signal }) =>
      UMSrequestClient.authenticatedRequest({
        method: "GET",
        url: `/teams/${teamID}/billing`,
        abortSignal: signal,
      }).then((res) => {
        return res.json<TeamsBillingResponseData>();
      }),

    staleTime: Infinity,
    enabled: Boolean(teamID),
  });

export const useUpdateBillingDetailsMutation = (teamID: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey, "update-billing-details", teamID],
    mutationFn: (billingDetails: BillingDetails) => {
      if (!teamID) {
        return Promise.reject(new Error("Update billin details failed: Team ID is undefined"));
      }

      return UMSrequestClient.authenticatedRequest({
        method: "PUT",
        url: `/teams/${teamID}/billing`,
        body: billingDetails,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData(
        [...billingQueryKey, teamID],
        (previousData: TeamsBillingResponseData | undefined) => {
          return {
            ...previousData,
            billingDetails: {
              ...variables,
            },
          };
        }
      );
    },
  });
};

export type StartPaymentUpdateResponseData = {
  paymentIntent: PaymentIntent;
};

export const useUpdateCardStartMutation = ({
  teamID,
  paymentType,
}: {
  teamID: string | undefined;
  paymentType: PaymentType | null | undefined;
}) =>
  useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey, "payment-update-start", teamID],
    mutationFn: () => {
      if (!teamID) {
        return Promise.reject(new Error("Update card start failed: Team ID is undefined"));
      }

      return UMSrequestClient.authenticatedRequest({
        method: "POST",
        url: `/teams/${teamID}/billing/update-card/start`,
        body: { paymentType },
      }).then((x) => x.json<StartPaymentUpdateResponseData>());
    },
    onError: (error) => {
      LogSentryException({ messagePrefix: "Update card start failed", error });
    },
  });

export type UpdateCardFinalizeRequestPayload = {
  authorizedPaymentIntentID: string;
};

export type FinalizePaymentUpdateResponseData = { primaryPaymentSource?: PaymentSource };

export const useUpdateCardFinalizeMutation = (
  teamID: string | undefined,
  options?: {
    onSuccess?: (data: FinalizePaymentUpdateResponseData) => void;
    onError?: (error: unknown) => void;
  }
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey, "payment-update-finalize", teamID],
    mutationFn: async ({
      data,
      teamID,
      paymentIntent,
    }: {
      data: PaymentUpdateFormData;
      teamID: string | undefined;
      paymentIntent: PaymentIntent | null;
    }) => {
      const { paymentType, card } = data;

      if (!paymentIntent) return Promise.reject(new Error("PaymentIntent is null"));

      const authorizedPaymentIntent = await authorizePaymentIntent({
        paymentType,
        paymentIntent,
        card,
      });

      if (!authorizedPaymentIntent) {
        return Promise.reject(new Error("Payment intent authorization failed"));
      }

      const requestBody: UpdateCardFinalizeRequestPayload = {
        authorizedPaymentIntentID: authorizedPaymentIntent.id,
      };

      const res = await UMSrequestClient.authenticatedRequest({
        method: "POST",
        url: `/teams/${teamID}/billing/update-card/finalize`,
        body: requestBody,
      });

      return res.json<FinalizePaymentUpdateResponseData>();
    },
    onSuccess: (data) => {
      queryClient.setQueryData([...billingQueryKey, teamID], { ...data });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
};
